/* eslint-disable no-unused-vars */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
export const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
export const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Skeleton loader component
export const SkeletonCard = () => (
  <Card>
    <div className="p-4 md:p-5">
      <div className="animate-pulse">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-24 mb-2"></div>
            <div className="h-8 bg-zinc-300 dark:bg-zinc-600 rounded w-16"></div>
          </div>
          <div className="p-3 bg-zinc-200 dark:bg-zinc-700 rounded-full">
            <div className="w-6 h-6 bg-zinc-300 dark:bg-zinc-600 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  </Card>
);

// Skeleton table row
export const SkeletonRow = ({ columns = 4 }) => (
  <tr>
    {[...Array(columns)].map((_, i) => (
      <td key={i} className="py-4 px-4">
        <div className="animate-pulse">
          <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-full"></div>
        </div>
      </td>
    ))}
  </tr>
);

// Action button component
export const ActionButton = ({ onClick, variant = 'primary', size = 'sm', children, className = '', disabled = false }) => {
  const variants = {
    primary: 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20',
    secondary: 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/20',
    success: 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20',
    warning: 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20',
    danger: 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20',
    accent: `text-[--accent] hover:bg-[--accent]/5`
  };

  const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2 text-base'
  };

  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.05 }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      onClick={onClick}
      disabled={disabled}
      className={`
        ${sizes[size]} 
        ${variants[variant]} 
        rounded-lg border border-white/20 dark:border-white/10 
        bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm 
        transition-all duration-200 font-medium
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{ '--accent': ACCENT }}
    >
      {children}
    </motion.button>
  );
};

// Status badge component
export const StatusBadge = ({ status, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
    success: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300',
    warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {status}
    </span>
  );
};

// Empty state component
export const EmptyState = ({ icon, title, description, action }) => (
  <div className="text-center py-12">
    <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800/50 flex items-center justify-center">
      {icon}
    </div>
    <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
      {title}
    </h3>
    <p className="text-zinc-500 dark:text-zinc-400 mb-6">
      {description}
    </p>
    {action}
  </div>
);

// Error state component
export const ErrorState = ({ error, onRetry }) => (
  <Card className="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20">
    <div className="p-4 md:p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
          <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
        </div>
        {onRetry && (
          <ActionButton onClick={onRetry} variant="danger" size="sm">
            Retry
          </ActionButton>
        )}
      </div>
    </div>
  </Card>
);

// Main inventory layout component
const InventoryLayout = ({ children, title, subtitle, action, currentTab = 'dashboard', showTabs = true }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Tab navigation
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin/inventory', icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
    { id: 'products', label: 'Products', path: '/admin/inventory/products', icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    )},
    { id: 'suppliers', label: 'Suppliers', path: '/admin/inventory/suppliers', icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )},
    { id: 'settings', label: 'Settings', path: '/admin/inventory/settings', icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )}
  ];

  const handleTabClick = (path) => {
    navigate(path);
  };

  // Determine current tab from location
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/products')) return 'products';
    if (path.includes('/suppliers')) return 'suppliers';
    if (path.includes('/adjustments')) return 'adjustments';
    if (path.includes('/transfers')) return 'transfers';
    if (path.includes('/settings')) return 'settings';
    return 'dashboard';
  };

  const activeTab = getCurrentTab();

  return (
    <div className="w-full">
      {/* Desktop Header */}
      <div className="hidden md:block mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              {title || 'Inventory Management'}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {subtitle || 'Manage your inventory and track stock levels'}
            </p>
          </div>
          {action}
        </div>
      </div>

      {/* Desktop Tab Navigation */}
      {showTabs && (
        <Card className="hidden md:block mb-6">
          <div className="p-4 md:p-5">
            <nav className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.path)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-2 py-2 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-[--accent] text-white shadow-lg'
                      : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/50 dark:hover:bg-zinc-800/50'
                  }`}
                  style={{ '--accent': ACCENT }}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </motion.button>
              ))}
            </nav>
          </div>
        </Card>
      )}

      {/* Mobile Top Navigation Header
          NOTE: must NOT be sticky/fixed; should scroll normally on mobile */}
      {showTabs && (
        <div className="md:hidden sticky top-0 -mx-4 -mt-4 z-40 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-b border-white/10 dark:border-white/10">
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Inventory</h2>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Navigate sections</p>
              </div>
            </div>
          </div>
          <div className="px-3 pb-4">
            <div className="flex items-center gap-1 flex-nowrap w-full" aria-label="Inventory sections">
              {tabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.path)}
                  whileTap={{ scale: 0.97 }}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-2 py-2 rounded-xl text-[11px] font-medium whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? 'bg-[--accent] text-white shadow'
                      : 'text-zinc-600 dark:text-zinc-400 bg-white/60 dark:bg-zinc-800/60 border border-white/20 dark:border-white/10'
                  }`}
                  style={{ '--accent': ACCENT }}
                >
                  <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{tab.icon}</span>
                  <span className="truncate">{tab.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content - Add bottom padding for mobile nav */}
      <div className="space-y-6 pb-24 md:pb-0">
        {children}
      </div>

      {/* Local utility: hide horizontal scrollbar on mobile tabs */}
      <style>{`
        .hc-hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hc-hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default InventoryLayout;
